<template>
  <div class="page">
    <!--Header-->
    <div class="page-header flex-shrink-0 bg-white border-bottom">
      <div class="container-fluid px-md-4 px-3">
        <div class="row no-gutters d-flex justify-content-between">
          <h3 class="mb-0"><i class="fas fa-file-signature"></i>&nbsp;Insurance Policies (Beta)<b-spinner class="ml-2"
              v-show="isBusy" small label="Small Spinner"></b-spinner></h3>
          <div class="bg-black-10 p-1 border rounded border-1 d-flex align-items-center">
            <router-link title="View by property" to="/propertyinsurancepolicies" class="btn btn-link text-dark px-3 py-1"
              v-if="true">View By Property</router-link>
            <span class="bg-white rounded px-3 py-1 text-dark shadow-sm">View By Policy</span>
          </div>
          <b-form inline>
            <div>
              <router-link to="/addeditinsurancepolicy" class="btn btn-success" v-if="true"><i class="fas fa-plus"></i>
                Add
                <span class="d-md-inline d-none">Insurance
                  Policy</span></router-link>
            </div>
          </b-form>
        </div>
      </div>
    </div>
    <!--Filters/Search-->
    <div class="container-fluid px-md-4 px-3 pt-2 pb-3 bg-white border-bottom mb-3">
      <div class="row no-gutters">
        <div class="col-lg-4 col-md-4">
          <label class="control-label">Property</label>
          <b-form-select v-model="propertyId" :options="properties" @change="filterByPropertyId">
          </b-form-select>
        </div>
      </div>
    </div>

    <!--Table-->
    <div v-show="policies.length" class="container-fluid flex-grow-1 overflow-auto px-md-4 px-3">
      <b-table id="policies-table" bordered class="mb-5 rg-table" :items="policyProvider" :fields="fields"
        :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :busy.sync="isBusy">


        <template v-slot:cell(number)="data">
          {{ data.item.number }}
        </template>
        <template v-slot:cell(policyStructureType)="data">
          {{ getPolicyStructureTypeName(data.item.policyStructureType) }}
        </template>
         <template v-slot:cell(carrierName)="data">
          {{ data.item.carrierName }}
        </template>
        <template v-slot:cell(startDate)="data">
          {{ data.item.startDate | formatDateNoTime }}
        </template>
        <template v-slot:cell(endDate)="data">
          <span :class="{ 'text-danger': data.item.expiringInDays <= 30 }">
            {{ data.item.endDate | formatDateNoTime }}
          </span>
        </template>
        <!-- NEW: Property Assignment Column -->
        <template v-slot:cell(propertyAssignment)="data">
          <div v-if="data.item.applyToAllProperties">
            <b-badge variant="success">
              <i class="fas fa-globe"></i> All Properties
            </b-badge>
          </div>
          <div v-else-if="data.item.propertyGroupIds && data.item.propertyGroupIds.length > 0">
            <b-badge variant="info">
              <i class="fas fa-layer-group"></i> {{ data.item.propertyGroupIds.length }} Group{{ data.item.propertyGroupIds.length !== 1 ? 's' : '' }}
            </b-badge>
          </div>
          <div v-else-if="data.item.propertyIds && data.item.propertyIds.length > 0">
            <b-badge variant="primary">
              <i class="fas fa-building"></i> {{ data.item.propertyIds.length }} Propert{{ data.item.propertyIds.length !== 1 ? 'ies' : 'y' }}
            </b-badge>
          </div>
          <div v-else>
            <b-badge variant="warning">
              <i class="fas fa-exclamation-triangle"></i> No Assignment
            </b-badge>
          </div>
        </template>
        <template v-slot:cell(totalInsuredValue)="data">
          {{ data.item.totalInsuredValue | formatCurrency }}
        </template>
        <template v-slot:cell(premiumPercentageChange)="data">
          <span v-if="data.item.premiumPercentageChange != null"
            :class="data.item.premiumPercentageChange >= 0 ? 'text-danger' : 'text-success'">
            {{ (data.item.premiumPercentageChange * 100).toFixed(1) }}%
          </span>
          <span v-else class="text-muted">—</span>
        </template>
        <template v-slot:cell(premium)="data">
          {{ data.item.premium | formatCurrency }}
        </template>
        <template v-slot:cell(premiumAnnualized)="data">
          {{ data.item.premiumAnnualized | formatCurrency }}
        </template>
        <template v-slot:cell(coverageAmount)="data">
          {{ data.item.coverageAmount | formatCurrency }}
        </template>
        <template v-slot:cell(deductible)="data">
          {{ data.item.deductible | formatCurrency }}
        </template>
        <template v-slot:cell(id)="data">
          <div class="d-flex">
            <router-link title="Edit Policy" :to="{ name: 'AddEditInsurancePolicy', params: { id: data.item.id }}"
              class="btn btn-link"><i class="fas fa-edit"></i></router-link>
            <div class="btn btn-link"><i class="fas fa-trash text-danger"
                @click="doDeleteInsurancePolicy(data.item.id)"></i></div>
          </div>
        </template>
      </b-table>
    </div>
    <div class="no-results d-flex justify-content-center align-items-center p-4 text-black-50 mx-4"
      v-if="policies.length == 0">No results matching filters</div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
// component code
export default {
  name: 'InsurancePolicies',
  data() {
    return {
      isBusy: false,
      sortBy: 'carrierName',
      sortDesc: false,
      fields: [],
      policies: [],
      policyStructureTypes: [
        { key: 0, value: 'Primary' },
        { key: 1, value: 'Excess' },
        { key: 2, value: 'Umbrella' },
        { key: 3, value: 'Package' },
        { key: 4, value: 'Deductible Buyback' },
        { key: 5, value: 'Diff. in Conditions' }
      ],
      properties: [],
      propertyId: null,
    }
  },
  created() {
    this.fields = [
      // { key: 'policySummary', label: 'Policy', sortable: true },
      { key: 'number', label: 'Number', sortable: true },
      { key: 'policyStructureType', label: 'Structure', sortable: true },
      { key: 'carrierName', label: 'Carrier Name', sortable: true },
      { key: 'startDate', label: 'Start Date', sortable: true },
      { key: 'endDate', label: 'End Date', sortable: true },
      { key: 'propertyAssignment', label: 'Property Assignment', sortable: false }, // NEW
      { key: 'premium', label: 'Premium', sortable: true },
      { key: 'premiumAnnualized', label: 'Prem. Annualized', sortable: true },
      { key: 'premiumPercentageChange', label: 'YoY %', sortable: true },
      { key: 'totalInsuredValue', label: 'TIV', sortable: true },
      { key: 'coverageAmount', label: 'Coverage Amount', sortable: true },
      { key: 'deductible', label: 'Deductible', sortable: true },
      { key: 'id', label: 'Actions' } 
    ]

    // Update Property Filter from localStorage
    let fpi = localStorage.getItem('filter-property-id')
    if (fpi != null) {
      this.propertyId = fpi
    }
  },
  methods: {
    ...mapActions('company', ['getInsurancePolicies', 'deleteInsurancePolicy', 'getProperties']),
    async policyProvider (ctx) {
      this.isBusy = true
      console.log(
        'policy table ctx is: ' +
        JSON.stringify(ctx, null, 2)
      )
      // var request = { sortBy: ctx.sortBy, sortDirection: ctx.sortDesc ? 'desc' : 'asc', search: ctx.filter }
      if(this.properties.length === 0){
        let result = await this.getProperties()
        if (result && result.data && result.data.properties) {
          this.properties = result.data.properties.map(p => ({value: p.id, text: p.name}))
          this.properties.splice(0, 0, {text: 'All', value: null})
        }
      }
      // Must return a promise that resolves to an array of items
      let request = { modifiers: { propertyId: this.propertyId }}
      return this.getInsurancePolicies(request).then(r => {
          // Pluck the array of items off our axios response
          const items = r.data.policies
          // Flatten nested/renamed fields so b-table can sort/display them directly
          items.forEach(item => {
            // computed.premiumPercentageChange is nested
            item.premiumPercentageChange = (item.computed && item.computed.premiumPercentageChange != null)
              ? item.computed.premiumPercentageChange
              : null
            // API field is standardDeductible; table key is deductible
            item.deductible = item.standardDeductible != null ? item.standardDeductible : 0
            // New records use policyLimit; legacy records have coverageAmount
            item.coverageAmount = (item.policyLimit != null && item.policyLimit > 0)
              ? item.policyLimit
              : item.coverageAmount
          })
          console.log(
          'Returned Policy Data is: ' +
          JSON.stringify(items, null, 2)
        )

        // If ctx.sortBy is set, sort items locally
        if (ctx.sortBy) {
          items.sort((a, b) => {
            let aVal = a[ctx.sortBy]
            let bVal = b[ctx.sortBy]

            // Fallback for undefined values
            if (aVal === undefined) aVal = ''
            if (bVal === undefined) bVal = ''

            // If it's a string, compare case-insensitively
            if (typeof aVal === 'string' && typeof bVal === 'string') {
              aVal = aVal.toLowerCase()
              bVal = bVal.toLowerCase()
            }

            if (aVal < bVal) return ctx.sortDesc ? 1 : -1
            if (aVal > bVal) return ctx.sortDesc ? -1 : 1
            return 0
          })
        }
        this.isBusy = false
        this.policies = items
        // Must return an array of items or an empty array if an error occurred
        return items || []
      })
    },
    getPolicyTypeNameFromKey(key) {
      if (key === null || key === undefined) return '—'
      var t = this.policyTypes.find(pt => pt.key === key)
      return t ? t.value : 'Unknown'
    },
    getPolicyStructureTypeName(key) {
      if (key === null || key === undefined) return '—'
      var t = this.policyStructureTypes.find(pt => pt.key === key)
      return t ? t.value : 'Unknown'
    },
    async doDeleteInsurancePolicy(policyId) {
      let result = confirm('Are you sure want to delete this policy?')
      if(result) {
        await this.deleteInsurancePolicy(policyId)
      let index = this.policies.findIndex(p => p.id == policyId)
      this.$root.$emit('bv::refresh::table', 'policies-table')
      this.$toasted.success('Insurance Policy Deleted.', { duration: 4000 })      }
    },
    filterByPropertyId (propertyId) {
      this.propertyId = propertyId
      if(propertyId == null) {
        localStorage.removeItem('filter-property-id')
      } else {
        localStorage.setItem('filter-property-id', propertyId)
      }

      this.$root.$emit('bv::refresh::table', 'policies-table')
    },
  }
}

</script>

<style scoped>

.no-results {
  border: 1px dashed #ccc;
  border-radius: 10px;
  background: #f9f9f9;
}

</style>
